import type { CatalogItem } from '../api';
import { Badge, Button } from '../components/ui';
import { findItem, isSelected, setManyItems, toggleItem, updateItem, type Draft } from './draft';

/**
 * Equipment to get back. The requester lists what the person has and the serial
 * numbers they know; IT records what actually came back and in what state, at
 * completion. Two different people, two different moments, so this step only
 * captures the first half.
 */
export function StepReturn({
  entries,
  draft,
  onChange,
}: {
  entries: CatalogItem[];
  draft: Draft;
  onChange: (next: Draft) => void;
}) {
  const listed = entries.filter((entry) => isSelected(draft, entry.id)).length;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">Hardware to return</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600 dark:text-slate-400">
            What are they holding? Add serial numbers where you know them; it is far easier to find now than
            after they have gone. IT records what actually comes back.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={listed > 0 ? 'brand' : 'neutral'}>{listed} listed</Badge>
          <Button size="sm" variant="ghost" onClick={() => onChange(setManyItems(draft, entries, false))}>
            Clear
          </Button>
        </div>
      </div>

      <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
        {entries.map((entry) => {
          const on = isSelected(draft, entry.id);
          const item = findItem(draft, entry.id);

          return (
            <li key={entry.id} className="px-4 py-2.5">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(event) => onChange(toggleItem(draft, entry, event.target.checked))}
                  className="h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 dark:border-slate-600"
                />
                <span className="min-w-0 flex-1 truncate text-sm text-slate-900 dark:text-slate-100">
                  {entry.name}
                </span>
              </label>

              {on && (
                <input
                  value={item?.assetDetails ?? ''}
                  onChange={(event) => onChange(updateItem(draft, entry.id, { assetDetails: event.target.value }))}
                  placeholder="Brand, model or serial number"
                  aria-label={`Details for ${entry.name}`}
                  className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
